import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { pageFieldSx, pageHeroShellSx } from "../../styles/pageModernStyles";
import { getAllUsers, getUserById } from "../../api/User/userApi";
import {
  getUserCountryMeta,
  groupUsersByCountry,
  USER_COUNTRY_META,
  USER_PERMISSION_KEYS,
} from "../../utils/userPermissionsUtils";
import UserPermissionCard from "./UserPermissionCard";

const UserPermissions = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const [users, setUsers] = useState([]);
  const [permissionsMap, setPermissionsMap] = useState({});
  const [openCard, setOpenCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState(null);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const isOriginal = u.username?.toUpperCase().startsWith("ORIGINAL");
      const isInactive = u.ACTIVO === "N" || u.activo === "N";
      if (isOriginal || isInactive) return false;

      const country = getUserCountryMeta(u);
      if (countryFilter && country.key !== countryFilter) return false;
      if (!q) return true;

      return (
        String(u.username || "")
          .toLowerCase()
          .includes(q) ||
        String(u.id).includes(q) ||
        String(u.email || u.EMAIL || "")
          .toLowerCase()
          .includes(q) ||
        country.label.toLowerCase().includes(q)
      );
    });
  }, [users, search, countryFilter]);

  const countryGroups = useMemo(
    () => groupUsersByCountry(filteredUsers),
    [filteredUsers],
  );

  const countryCounts = useMemo(() => {
    const counts = {};
    for (const u of users) {
      const isOriginal = u.username?.toUpperCase().startsWith("ORIGINAL");
      const isInactive = u.ACTIVO === "N" || u.activo === "N";
      if (isOriginal || isInactive) continue;
      const key = getUserCountryMeta(u).key;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [users]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const decoded = jwtDecode(token || "");
        const me = await getUserById(decoded.userId ?? decoded.id);
        if (!me?.Permiso_Users) {
          navigate("/mi-dashboard", { replace: true });
          return;
        }

        const data = await getAllUsers();
        setUsers(data);

        const perms = {};
        for (const u of data) {
          const info = await getUserById(u.id);
          const mapped = USER_PERMISSION_KEYS.reduce((acc, key) => {
            acc[key] = !!info[key];
            return acc;
          }, {});
          perms[u.id] = mapped;
        }
        setPermissionsMap(perms);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  const activeCount = filteredUsers.filter((u) =>
    Object.values(permissionsMap[u.id] || {}).some(Boolean),
  ).length;

  const renderUserCard = (user) => (
    <UserPermissionCard
      key={user.id}
      user={user}
      initialPermissions={permissionsMap[user.id] || {}}
      open={openCard === user.id}
      onToggle={() => setOpenCard(openCard === user.id ? null : user.id)}
      onSaved={(nextPerms) =>
        setPermissionsMap((prev) => ({
          ...prev,
          [user.id]: nextPerms,
        }))
      }
    />
  );

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        pt: { xs: 1.25, md: 2 },
        px: { xs: 1.25, md: 2 },
        pb: { xs: 10, md: 3 },
        ...contentMargin,
      }}
    >
      <Box sx={pageHeroShellSx(theme, { sticky: true })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#5e35b1", 0.14),
                  color: "#5e35b1",
                  border: "1px solid",
                  borderColor: alpha("#5e35b1", 0.28),
                  boxShadow: `0 6px 18px ${alpha("#5e35b1", 0.22)}`,
                }}
              >
                <AdminPanelSettingsOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1, lineHeight: 1.1 }}
                >
                  ADMIN · ACCESS
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: "1.35rem", sm: "2rem" },
                  }}
                >
                  {t("sidebar.usersPermissions", {
                    defaultValue: "Users Permissions",
                  })}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75, rowGap: 0.35 }}
                >
                  <Chip
                    label={t("userPermissions.userCount", {
                      defaultValue: "{{count}} users",
                      count: filteredUsers.length,
                    })}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: "0.68rem",
                      bgcolor: alpha("#1565c0", 0.12),
                      color: "#1565c0",
                      border: `1px solid ${alpha("#1565c0", 0.3)}`,
                    }}
                  />
                  <Chip
                    label={t("userPermissions.withAccess", {
                      defaultValue: "{{count}} with access",
                      count: activeCount,
                    })}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: "0.68rem",
                      bgcolor: alpha("#2e7d32", 0.12),
                      color: "#2e7d32",
                      border: `1px solid ${alpha("#2e7d32", 0.3)}`,
                    }}
                  />
                  <Chip
                    label={`${Math.max(filteredUsers.length - activeCount, 0)} sin acceso`}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: "0.68rem",
                      bgcolor: alpha("#c62828", 0.1),
                      color: "#c62828",
                      border: `1px solid ${alpha("#c62828", 0.28)}`,
                    }}
                  />
                  {Object.entries(USER_COUNTRY_META)
                    .filter(([key]) => countryCounts[key])
                    .map(([key, meta]) => {
                      const selected = countryFilter === key;
                      return (
                        <Chip
                          key={key}
                          label={`${meta.flag} ${meta.label} · ${countryCounts[key]}`}
                          size="small"
                          onClick={() =>
                            setCountryFilter(selected ? null : key)
                          }
                          sx={{
                            height: 22,
                            fontWeight: 800,
                            fontSize: "0.68rem",
                            cursor: "pointer",
                            bgcolor: selected
                              ? alpha(meta.color, 0.2)
                              : alpha(meta.color, 0.1),
                            color: meta.color,
                            border: `1px solid ${alpha(meta.color, selected ? 0.55 : 0.28)}`,
                          }}
                        />
                      );
                    })}
                </Stack>
              </Box>
            </Stack>

            <TextField
              size="small"
              placeholder={t("userPermissions.search", {
                defaultValue: "Search user…",
              })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchOutlinedIcon
                    sx={{ fontSize: 18, color: "text.secondary", mr: 0.75 }}
                  />
                ),
              }}
              sx={{
                ...pageFieldSx(theme),
                width: { xs: "100%", md: 280 },
                flexShrink: 0,
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            {t("userPermissions.heroHint", {
              defaultValue:
                "Usuarios agrupados por país. Pulsa uno para revisar permisos.",
            })}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("userPermissions.empty", {
              defaultValue: "No users match your search.",
            })}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {countryGroups.map((group) => (
            <Box key={group.key}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.25, px: 0.25 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: group.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    fontSize: "0.78rem",
                    color: group.color,
                  }}
                >
                  {group.flag} {group.label}
                </Typography>
                <Chip
                  label={group.users.length}
                  size="small"
                  sx={{
                    height: 20,
                    minWidth: 20,
                    fontWeight: 800,
                    fontSize: "0.65rem",
                    bgcolor: alpha(group.color, 0.12),
                    color: group.color,
                    border: `1px solid ${alpha(group.color, 0.28)}`,
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    bgcolor: alpha(group.color, 0.18),
                  }}
                />
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(auto-fill, minmax(240px, 1fr))",
                  },
                  gap: { xs: 1.25, sm: 2 },
                }}
              >
                {group.users.map((user) => renderUserCard(user))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default UserPermissions;
